import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import { ChevronRight, Package } from "lucide-react";
import { ApiError } from "../../api/client";
import { catalogueApi } from "../../api/catalogue";
import { Breadcrumb } from "../../components/catalogue/Breadcrumb";
import { ListingResults, type ListingSort } from "../../components/catalogue/ListingResults";
import { EmptyState, LoadError } from "../../components/catalogue/Notices";
import { PageWidth } from "../../components/catalogue/StoreShell";
import { Button, Icon } from "../../components/ui";
import { useListingView } from "../../lib/listingView";
import { CardAddButton } from "./CardAddButton";
import { NotFound } from "./NotFoundRoute";
import { usePageTitle } from "./usePageTitle";

/** PRD screen 5, the index: kinds of thing, for items that aren't tied to one machine. */
export function ProductTypesRoute() {
  usePageTitle("Supplies by type");
  const types = useQuery({ queryKey: ["catalogue", "product_types"], queryFn: catalogueApi.productTypes });
  return (
    <PageWidth className="pt-8">
      <h1 className="mb-2 type-h1 [font-size:var(--text-2xl)]">Supplies by type</h1>
      <p className="mb-8 max-w-[40rem] type-body text-text-muted">Oil, gloves, filters and other things that aren't tied to one machine. For a machine part, start from the machine instead.</p>
      {types.isPending ? (
        <p className="type-body text-text-muted">Loading…</p>
      ) : types.isError ? (
        <LoadError onRetry={() => types.refetch()} />
      ) : types.data.length === 0 ? (
        <EmptyState icon={Package} title="Nothing here yet" action={<Link to="/equipment-categories">Browse machines</Link>}>
          Supplies are being added. Machine parts are listed under their machine.
        </EmptyState>
      ) : (
        <ul className="m-0 grid list-none gap-3 p-0 sm:grid-cols-2 lg:grid-cols-3">
          {types.data.map((type) => (
            <li key={type.id}>
              <Link to={`/product-types/${type.id}`} className="flex min-h-16 items-center gap-4 rounded-md border border-border-subtle bg-surface-card px-5 py-3 shadow-xs hover:border-green-700">
                <span className="min-w-0 flex-1">
                  <span className="block type-h4 [font-size:var(--text-base)] text-text-strong">{type.name}</span>
                  <span className="block type-small text-text-muted">
                    {type.listing_count} {type.listing_count === 1 ? "item" : "items"}
                  </span>
                </span>
                <Icon icon={ChevronRight} size={18} className="text-ink-400" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </PageWidth>
  );
}

/** PRD screen 5, one type: its listings, a page at a time, as cards or a list (KALI-33). */
export function ProductTypeRoute() {
  const id = Number(useParams().id);
  const [view, setView] = useListingView();
  const [sort, setSort] = useState<ListingSort>("default");
  const type = useQuery({ queryKey: ["catalogue", "product_type", id], queryFn: () => catalogueApi.productType(id) });
  const pages = useInfiniteQuery({
    queryKey: ["catalogue", "product_type", id, "listings"],
    queryFn: ({ pageParam }) => catalogueApi.productTypeListings(id, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  usePageTitle(type.data?.name ?? null);

  // Sorting by size or price needs the whole department, not just the pages loaded so far.
  const sortingAll = sort !== "default" && pages.hasNextPage;
  const { fetchNextPage, isFetchingNextPage } = pages;
  useEffect(() => {
    if (sortingAll && !isFetchingNextPage) fetchNextPage();
  }, [sortingAll, isFetchingNextPage, fetchNextPage]);

  if (type.isError && type.error instanceof ApiError && type.error.status === 404) return <NotFound />;
  if (type.isError && !type.data) {
    return (
      <PageWidth className="py-10">
        <LoadError onRetry={() => type.refetch()} />
      </PageWidth>
    );
  }
  const rows = pages.data?.pages.flatMap((page) => page.data) ?? [];

  return (
    <PageWidth className="pt-6">
      <Breadcrumb items={[{ label: "Supplies by type", to: "/product-types" }, { label: type.data?.name ?? "…" }]} />
      <h1 className="mt-4 mb-1 type-h1 [font-size:var(--text-2xl)]">{type.data?.name ?? " "}</h1>
      {type.data && (
        <p className="mb-6 type-body text-text-muted">
          {type.data.listing_count} {type.data.listing_count === 1 ? "item" : "items"}. Prices include tax.
        </p>
      )}
      {pages.isPending ? (
        <p className="type-body text-text-muted">Loading…</p>
      ) : pages.isError ? (
        <LoadError onRetry={() => pages.refetch()} />
      ) : rows.length === 0 ? (
        <EmptyState icon={Package} title="Nothing of this type is on sale right now" />
      ) : sortingAll ? (
        <p className="type-body text-text-muted">Loading every {type.data?.name ?? "part"} to sort them…</p>
      ) : (
        <ListingResults
          listings={rows}
          defaultSortLabel="Name"
          sort={sort}
          onSort={setSort}
          view={view}
          onView={setView}
          add={(listing, compact) => <CardAddButton listing={listing} compact={compact} />}
        />
      )}
      {pages.hasNextPage && sort === "default" && (
        <Button variant="outline" className="mt-5" onClick={() => pages.fetchNextPage()} disabled={pages.isFetchingNextPage}>
          {pages.isFetchingNextPage ? "Loading…" : "Load more"}
        </Button>
      )}
    </PageWidth>
  );
}
